/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import { useEffect, useState } from "react";
import { useQueryListContext } from "./QueryListContext";
import { Loading } from "./Loading";
import { Error } from "./Error";
import { ListControl } from "./ListControl";
import { ListSearch } from "./ListSearch";
import { ListColumns } from "./ListColumns";
import { ListRecords } from "./ListRecords";
import { Row, Col, Table, Card, Modal, Button } from "react-bootstrap";
import { Edit } from "./Edit";
import { useAuth } from "../../../contexts/AuthContext";
import { CastType, DynamicObject, IFrontEndOnly, IQuery, IQueryColumnDetails, IQueryListComms, IQuerySelectData, IWhere, QueryListProperties } from "../Interfaces";
import { CheckCircle, ChevronDown, ChevronUp, Download, RotateCcw } from "lucide-react";
import ShowErrorAlert from "../../ShowErrorAlert";
import { formatDateFull } from "../utils/AppUtil";
import { CANCELLED } from "dns";



export interface IStockAdjustmentsFailedTransaction {
    m24l_reference: string,
    function_call_id: string, 
    extended_message: string | null, 
    completed_at: string,
    created: string, 
}

export const QueryList: React.FC<QueryListProperties> = (props) => {


    const comms: IQueryListComms ={
        query: `${process.env.REACT_APP_BASE_URL}/${"query/data"}`,
        schema: `${process.env.REACT_APP_BASE_URL}/${"query/schema"}` 
    }


    const {isAuthenticated, token, fetchData, fetchDataMulti, responseErrorMessage, showErrorAlert, responseStatus, userDetail}  = useAuth();

    const {ctxData, getData, setData} = useQueryListContext();

    // Column Details
    const [columnDetails, setColumnDetails] = useState<IQueryColumnDetails[]>([]);

    // Data Records
    const [dataRecords, setDataRecords] = useState<DynamicObject[]>([]);

    // Search Fields
    const [search, setSearch] = useState<Map<string, string>>(new Map<string, string>());

    // Page Support Hooks
    const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);

    // Pagination Hooks:
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isOpen, setIsOpen] = useState(false);

    const [activeStatusKey, setActiveStatusKey] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [shouldFadeOut, setShouldFadeOut] = useState(false);

    //Failed Transactions states section
    const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
    const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
    const [submitRefreshDataCancelledTransactions, setSubmitRefreshDataCancelledTransactions] = useState<boolean>(false); 

    const defaultSelectedStockAdjustmentFailedTransaction = { m24l_reference: "", function_call_id: "", extended_message: "", completed_at: "",  created: "" }; 
    const [stockAdjustmentFailedTransactionsData, setStockAdjustmentFailedTransactionsData] = useState<IStockAdjustmentsFailedTransaction[]>([]);
    const [selectedStockAdjustmentFailedTransaction, setSelectedStockAdjustmentFailedTransaction] = useState<IStockAdjustmentsFailedTransaction>(defaultSelectedStockAdjustmentFailedTransaction);
    const [stockAdjustmentFailedTransactionsRerunModal, setStockAdjustmentFailedTransactionsRerunModal] = useState<boolean>(false);
    const [stockAdjustmentFailedTransactionsHandledModal, setStockAdjustmentFailedTransactionsHandledModal] = useState<boolean>(false);


    const [expanded, setExpanded] = useState(false);

    // -> failed transactions -> stockAdjustments
    const defaultQueryStateFailedTransactionsStockAdjustments: IQuerySelectData = {
            appCode: "ares",
            selectCode: "sa-m24l-stock-adjustment",  
            where: `handled_status IS NOT DISTINCT FROM :handled_status`,  
            params: {'handled_status': "REQUIRED"}
    }; 
    const [querySelectStateFailedStockAdjustment, setQuerySelectStateFailedStockAdjustment] = useState<IQuerySelectData>(defaultQueryStateFailedTransactionsStockAdjustments);

    const toggleDropdown = () => {
          setIsOpen(!isOpen);
    };

    const frontEndOnly: IFrontEndOnly = {
        listTitle: props.listTitle,
        recordTitle: props.recordTitle,
        editTableName: props.editTableName,
        editHasCompoundKey: props.editHasCompoundKey
    }

    // Query State Hook
    const [queryState, setQueryState] = useState<IQuery>({
        tableName: props.tableName,
        select: props.select,
        groupBy: props.groupBy,
        join: props.join,
        orderBy: props.orderBy,
        where: props.where,
        mainTableOnly: false,
        includeExtraColumns: false,
        includeControlColumns: false,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        pageNum: 0,
        pageSize: 100,
        frontEndOnly: frontEndOnly
    }); 

    useEffect(() => {
        setData("comms", comms);
        setData("queryState", queryState);   
        setData("modal", props.modal);     
    }, []);

    useEffect(() => {
        const endpoints: string[] = [comms.schema, comms.query];
        
        setIsLoading(true);

        fetchQueryData(endpoints, processData).catch((error: any) => {
            setIsLoading(false);
            setHttpError(error.message);
        }).finally(() => {
            setIsLoading(false);
        });

    }, [queryState, submitRefreshDataCancelledTransactions]);

    // preventing the download spinner from spinning
    useEffect(() => {
        if(responseStatus === "400" || responseStatus === "401"){
            setDownloading(false);
        }
        if(showErrorAlert){
            setDownloading(false);
        }        
    }, [responseStatus, showErrorAlert])

    useEffect(() => {
        if (downloadComplete && !downloading) {
            // Start fading after 4.5s
            const fadeTimer = setTimeout(() => {
                setShouldFadeOut(true);
            }, 4500);

            // Hide alert completely after 5s
            const hideTimer = setTimeout(() => {
                setDownloadComplete(false);
                setShouldFadeOut(false); // reset for future
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [downloadComplete, downloading]);

    useEffect(() => {
        if (props.showFailedTransactionsTable && props.tableName === "sa_m24l_stock_adjustment") {
            fetchData(queryUrlSelectNoCount, { method: "POST", body: JSON.stringify(querySelectStateFailedStockAdjustment) }, processDataFailedStockAdjustment);
        }
    }, [props.showFailedTransactionsTable, props.tableName, submitRefreshData, userDetail?.roles]);


    const processData = (result: any) => {

        // console.log(`Testing something `);
        // console.log(`========================================`);
        // console.log(result);
        // console.log(`========================================`);

        if(result && result.length === 2){

            const columnDetails: IQueryColumnDetails[] = result[0];
            const searchColumns: Map<string, string> = search;

            if(search.size === 0){
                for(const key in columnDetails) {
                    const columnDefinition:IQueryColumnDetails = columnDetails[key];
                    searchColumns.set(columnDefinition.fullColumnName as string, '');
                }
            }

            const data = result[1].data;
            const processedRecords: DynamicObject[] = [];
            const receivedUndefinedRecords: Array<Map<string, any>> = [];
            for(const key in data) {

                const recordData: DynamicObject = data[key]["data"];
                const recordControl: DynamicObject = data[key]["control"];
                const recordUndefined: Map<string, any> = data[key]["undefined"];

                // Merge recordData and recordControl together
                const concatRecord = {...recordData, ...recordControl};
                // console.log(JSON.stringify(concatRecord, null, 2));

                processedRecords.push(concatRecord);
                receivedUndefinedRecords.push(recordUndefined);
            }

            // Pagination 
            setPageNumber(result[1].pageNum);
            setPageSize(result[1].pageSize);
            setTotalRecords(result[1].numRecords);
            setTotalPages(Math.ceil(result[1].numRecords / result[1].pageSize));


            //console.log(columnDetails);
            setColumnDetails(columnDetails);
            setDataRecords(processedRecords);
            setSearch(searchColumns);          
        }
    }

    const fetchQueryData = async (endpoints: string[], processData: any) => {
        fetchDataMulti(endpoints, {method:"POST", body: JSON.stringify(queryState)}, processData);
    }


    // failed transactions for stock adjustments
    const processDataFailedStockAdjustment = (result: any) => {

        const stockAdjustmentsFailedTransactions: IStockAdjustmentsFailedTransaction[] = [];

        for (const key in result?.data) {
            const item = result.data[key];
            if (item?.data) {
                stockAdjustmentsFailedTransactions.push(item.data as IStockAdjustmentsFailedTransaction);
            }
        }
        
        stockAdjustmentsFailedTransactions.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    
        // Collapse table secion if empty reset expand
        if (stockAdjustmentsFailedTransactions.length === 0) {
            setExpanded(false);
        }
    
        setStockAdjustmentFailedTransactionsData(stockAdjustmentsFailedTransactions);
    }



    if(isLoading) {
        return(
            <Loading />
        )
    }

    if(httpError) {
        return(
            <Error error={httpError} />
        )
    }

    const handlePageClick = (pageNum: number) => {  
        const query = {...queryState}
        query.pageNum = pageNum;
        setQueryState(query);
    }

    const handleSizeClick = (event: React.FocusEvent<HTMLInputElement>) => {
        const query = {...queryState}
        query.pageSize = Number(event.target.value) ?? 20;
        setQueryState(query);
    } 


      // Function to handle date parsing and formatting
    function parseDate(value: string): string {
        let date: Date | null = null;

        // Try to parse the value in various formats
        const datePatterns = [
            /^\d{2}-\d{2}-\d{4}$/, // dd-mm-yyyy
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} [APM]{2}$/, // dd-mm-yyyy hh:mm:ss AM/PM
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // dd-mm-yyyy hh:mm:ss
            /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd (valid input)
        ];

        // Check if the value matches any of the patterns
        if (datePatterns[0].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [day, month, year] = value.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[1].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' '); 
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[2].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' ');
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[3].test(value)) {
            // Valid yyyy-mm-dd, no changes needed
            date = new Date(value);
        } else {
            // If no pattern matches, return the original value
            return value;
        }

        // If date is valid, format it as yyyy-mm-dd
        if (date && !isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
        }

        // If invalid date, return the original value
        return value;
    }


    const searchClicked = () => {
      let where: IWhere[] = [];

      search.forEach((value: string, column: string) => {
          if (!value || !value.length) return;

          // Check for date fields
          if (column === "created" || column === "updated") {
              const formattedValue = parseDate(value);
              if (formattedValue) {
                  where.push({
                      fullColumnName: column,
                      value: formattedValue,
                      operator: "EQ",
                      cast: CastType.DATE
                  });
              }
          } else {
              // Default string LIKE filter
              where.push({
                  fullColumnName: column,
                  value: `%${value}%`,
                  operator: "LIKE",
                  cast: CastType.STRING
              });
          }
      });    

      const query = { ...queryState };
      query.where = where;
      query.pageNum = 0;

      setQueryState(query);
      setActiveStatusKey(null);
  };

    const clearClicked = () => {
        const query = {...queryState}
        query.where = props.where;
        setQueryState(query);
        setSearch(new Map<string, string>()); 
        setActiveStatusKey(null);
    }    



    const renderStatusLegend = () => {

            if (!props.hasIntegrationStatusLegend) return null;
          
            return (
              <div className="position-relative integration-status-legend">
                <button onClick={toggleDropdown} className="btn-md btn btn-secondary d-flex align-items-center">
                  Integration Statuses
                  <svg xmlns="http://www.w3.org/2000/svg"  width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ verticalAlign: 'middle' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 14h-4v-2h1v-4h-1v-2h3v6h1v2z" />
                  </svg>
                </button>
                { isOpen && (
                  <div className="integration-status-legend-content">
                    {props.integrationstatusLegend?.map((item, index) => (
                      <div className="item-rows" key={index} >
                        <div style={{ width: '1rem',  height: '1rem', backgroundColor: item.color, borderRadius: '0.125rem' }} />
                        <p className="status-name">
                          {item.status}:
                        </p>
                        <p className="status-description">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        );
    }

    const handleFilterByStatusChange = (status: string | null) => {
      // Map of status configurations per table
        const tableStatusMap: Record<string, {fullColumnName: string; statuses: Record<string, string[]>}> = {
            sa_m24l_stock_adjustment: {
                fullColumnName: 'status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_PROCESS'],
                  CLOSED: ['PROCESSED']
                }
              },
              sa_m24l_return_request: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_SEND', 'SENT', 'ARRIVED', 'SUCCESS'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_item: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_SEND', 'SENT'],
                  CLOSED: ['SUCCESS']
                }
              },
              sa_m24l_stock_movement_in: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_SEND', 'ARRIVED', 'SENT', 'SUCCESS', 'TO_CLOSE'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_stock_movement_out: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_SEND', 'PICKING', 'DISPATCHED', 'SENT', 'SUCCESS', 'TO_CLOSE'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_pick_list: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  CANCELLED: ['CANCELLED'], 
                  PROCESSING: ['TO_SEND', 'PICKING', 'DISPATCHED', 'SENT', 'SUCCESS'],
                  CLOSED: ['CLOSED']
                }
              }
        };

        // Fallback in case tableName is missing or not matched
        const statusMap = tableStatusMap[props.tableName] ?? {
            ERROR: [''],
            CANCELLED: [''], 
            PROCESSING: [],
            CLOSED: []
        };

        const tableConfig = tableStatusMap[props.tableName];

        const statusValues = status && tableConfig ? tableConfig.statuses[status] ?? [] : [];
        const fullColumn = tableConfig?.fullColumnName ?? "integration_status"; // fallback just in case

        let where: IWhere[] = [];

        if (statusValues.length > 0) {
        where.push({
            fullColumnName: fullColumn,
            value: statusValues,
            operator: "IN",
            cast: CastType.STRING
        });
        }

        search.forEach((value, column) => {
        if (value && value.length && column !== fullColumn) {
            where.push({
            fullColumnName: column,
            value: '%' + value + '%',
            operator: "LIKE",
            cast: CastType.STRING
            });
        }
        });

        const newQueryState = { ...queryState, where, pageNum: 0 };
        setQueryState(newQueryState);
    };


        
    const handleExport = () => {

        setDownloading(true);
        const queryExportReportXLSXFormatUrl: string = `${process.env.REACT_APP_BASE_URL}/${"reports/xlsx"}`;
        const { pageNum, pageSize, frontEndOnly, groupBy, includeControlColumns, includeExtraColumns, isDirty, modifiedAfter, select, touchedBefore,   ...querySpecifiedFields } = queryState;

        // Determine table name
        const tableName = queryState.tableName;

        // Conditionally assign `join`
        if (tableName === 'sa_m24l_item' || tableName === 'sa_m24l_stock_adjustment') {
            querySpecifiedFields.join = []; // no joins
        } else {
            querySpecifiedFields.join = [{ relationship: 'lines', joinType: 'LEFT' }];
        }
        

        const filenameMap: Record<string, { template: string; report: string; queryOrderOveride?: (query: typeof querySpecifiedFields) => void }> = {
            sa_m24l_stock_movement_in: {
                template: "stock_movement_in_template.xlsx",
                report: "stock_movement_in_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
               },
            },
            sa_m24l_stock_movement_out: {
                template: "stock_movement_out_template.xlsx",
                report: "stock_movement_out_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
               },
            },
            sa_m24l_pick_list: {
                template: "pick_list_template.xlsx",
                report: "pick_list_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
               },
            },
            sa_m24l_item: {
                template: "item_master_template.xlsx",
                report: "item_master_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
                },
            },
            sa_m24l_stock_adjustment: {
                template: "stock_adjustment_template.xlsx",
                report: "stock_adjustment_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
                },
            },
            sa_m24l_return_request: {
                template: "return_request_template.xlsx",
                report: "return_request_report.xlsx",
                queryOrderOveride: (query) => {
                    query.orderBy = [
                    
                    ];
               },
            },
        };

        const defaultFilenames = { template: "default_template.xlsx", report: "default_report.xlsx" };

        // Pick appropriate filenames
        const { template, report, queryOrderOveride } = filenameMap[tableName] || defaultFilenames;

        if (queryOrderOveride) { queryOrderOveride(querySpecifiedFields); }

        const requestBody = { query: querySpecifiedFields };

        const urlWithParams = `${queryExportReportXLSXFormatUrl}?templateFilename=apps/ares/${template}&reportFilename=${report}`;

        // console.log(`====================================================`);
        // console.log(`Url:`);
        // console.log(urlWithParams);
        // console.log(`Request Body`);
        // console.log(requestBody);
        // console.log(`====================================================`);

        // Show banner
        setDownloadComplete(false);
  
        fetchData(urlWithParams, 
          {method:"POST", body: JSON.stringify(requestBody)}, 
          (result: any, status: string) => {
             if (status === "200" || status === "201") {
                handleExcelExport(result);
                setDownloadComplete(true);  // Change banner to "Downloaded!"
                setDownloading(false);      // Hide "Downloading..."
            } else {
                setDownloading(false);      // Hide "Downloading..."
                setDownloadComplete(false); // Hide success banner if error
                // Optionally, show error banner here
            }
          });
    }

   


    const handleExcelExport = async (result: any) => {
        
        const tableName = queryState.tableName; // Full table name from queryState

        // Abbreviated table names (short names)
        const tableNameMap: Record<string, string> = {
            sa_m24l_stock_movement_in: "stock_movement_in",
            sa_m24l_stock_movement_out: "stock_movement_out",
            sa_m24l_pick_list: "pick_list",
            sa_m24l_item: "item_master", 
            sa_m24l_stock_adjustment: "stock_adjustment", 
            sa_m24l_return_request: "return_request",
        };

        // Get the abbreviated table name if it exists in the map, otherwise use the full table name
        const abbreviatedTableName = tableNameMap[tableName] || tableName;

        // Use the abbreviated name for both the template and report
        const fileName = `${abbreviatedTableName}.xlsx`; // Use abbreviated table name

        // Create a download link for the Blob
        const url = URL.createObjectURL(result);
        // Create a temporary <a> element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Set the default filename here
        document.body.appendChild(a);
        a.click();
    }

    const toggleExpanded = () => setExpanded((prev) => !prev);

    //handle stock adjustment rerun click
    const handleFailedStockAdjustmentRerunClick = (stockAdjustment: IStockAdjustmentsFailedTransaction) => {
        setSelectedStockAdjustmentFailedTransaction(stockAdjustment);
        setStockAdjustmentFailedTransactionsRerunModal(true); 
    }
 
    //handle stock adjustment rerun reset click [RESET]
    const handleFailedStockAjdustmentRerunResetClick = () => {
        setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
        setStockAdjustmentFailedTransactionsRerunModal(false); 
    }

    //handle sumbit for stock adjustment rerun 
    const handleFailedStockAjdustmentRerunSubmit = () => {
        if(selectedStockAdjustmentFailedTransaction?.m24l_reference !== "" && selectedStockAdjustmentFailedTransaction?.function_call_id !== ""){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedStockAdjustmentFailedTransaction?.function_call_id}/rerun`, 
            {method:"POST", body: JSON.stringify({ })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
                    setSubmitRefreshData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
                }
            });
        } else {
            return; 
        }
    }

    //handle stock adjustment handle click
    const handleFailedStockAdjustmentHandledClick = (stockAdjustment: IStockAdjustmentsFailedTransaction) => {
        setSelectedStockAdjustmentFailedTransaction(stockAdjustment);
        setStockAdjustmentFailedTransactionsHandledModal(true)
    }

    //handle stock adjustment handled reset click [RESET]
    const handleFailedStockAjdustmentHandledResetClick = () => {
        setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
        setStockAdjustmentFailedTransactionsHandledModal(true)
    }

    //handle sumbit for stock adjustment handled 
    const handleFailedStockAdjustmentHandledSubmit = () => {
         if(selectedStockAdjustmentFailedTransaction?.m24l_reference !== "" && selectedStockAdjustmentFailedTransaction?.function_call_id !== ""){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedStockAdjustmentFailedTransaction?.function_call_id}/handle`, 
            {method:"POST", body: JSON.stringify({ })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
                    setSubmitRefreshData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedStockAdjustmentFailedTransaction(defaultSelectedStockAdjustmentFailedTransaction);
                }
            });
        } else {
            return; 
        }
    }

    const thStyle = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const thNewStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        borderTop: "1px solid #dee6ed",
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const tdStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };


    //handle canceld transaction
    const handleCancelTransaction = (user_id: string, document_number: string, document_type: string, transaction_number: string) => {

        if (user_id === "" || document_number === "" || document_type === "") return;
 
        // form data to be posted
        const formData  = { 
            user_id: user_id, 
            document_number: document_number, 
            document_type: document_type, 
            transaction_number: transaction_number 
        };

        fetchData(`${process.env.REACT_APP_BASE_URL}/functions/CANCEL-DOCUMENT/run`, 
        {method:"POST", body: JSON.stringify(formData)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
               setSubmitRefreshDataCancelledTransactions(prev => !prev)
            }
        });
    }

    return (
        <>
                {(getData("primaryKeyValue") === undefined) 
                ?
                    <>
                        { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 

                         {/* Show only while downloading and not errored */}
                        {downloading && !downloadComplete && !showErrorAlert && (
                        <div className="alert d-flex align-items-center py-2 px-3 mb-2" role="alert" style={{ backgroundColor: "#e6eefa", border: '1px solid rgb(222, 235, 237)', color: "#035a70" }}>
                            <span className="flex-grow-1 text-center">
                            Your report is currently being processed. Please wait...
                            </span>
                        </div>
                        )}

                        {/* Show success only if download succeeded */}
                        {downloadComplete && !downloading && !showErrorAlert && (
                        <div className={`alert d-flex align-items-center py-2 px-3 mb-2 ${shouldFadeOut ? 'fading-out' : ''}`} role="alert" 
                            style={{ backgroundColor: "#c7f5bf", border: "1px solid rgb(222, 235, 237)", color: "#037012", transition: "opacity 0.5s ease-in-out", opacity: shouldFadeOut ? 0 : 1 }}>
                            <span className="flex-grow-1 text-center">
                            Your report has been successfully downloaded.
                            </span>
                            <button type="button" 
                            style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', backgroundColor: '#c7f5bf', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                            aria-label="Close"
                            onClick={() => setDownloadComplete(false)} 
                            >
                            <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
                                <path stroke="#037012" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                            </svg>
                            </button>
                        </div>
                        )}
                        
                        <Card>
                            <Card.Header className="pb-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                             <Card.Title>{props.listTitle}</Card.Title>
                             <div className="d-flex align-items-center gap-2">

                               
                                    <div title={downloading ? 'Preparing report...' : 'Export'}>
                                        <button
                                            className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                            onClick={handleExport}
                                            disabled={downloading}
                                            style={{
                                                position: 'relative',
                                                padding: downloading ? '0.39rem 0.65rem' : '0.4rem 0.65rem', 
                                            }}
                                        >
                                            {downloading ? (
                                                
                                                <span className="spinner-border spinner-border-sm custom-spinner" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <Download size={16} />
                                            )}
                                        </button>
                                    </div>
                                    {renderStatusLegend()}

                                     { /* toggle button for the failed transactions */ }
                                     {props.showFailedTransactionsTable && stockAdjustmentFailedTransactionsData.length > 0 && (
                                        <button
                                                className="btn p-1 d-flex align-items-center justify-content-center"
                                                onClick={toggleExpanded}
                                                title={expanded ? "Hide failed transactions" : "Show failed transactions"}
                                                style={{ border: "1px solid #c10000", background: "transparent", borderRadius: "50%", color: "#c10000", width: "22px", height: "22px"}}
                                                >
                                                {expanded ? (
                                                    <ChevronUp size={20} strokeWidth={2.2} />
                                                ) : (
                                                    <ChevronDown size={20} strokeWidth={2.2} />
                                                )}
                                        </button>
                                    )}
                                </div>
                              </div>

                                { /* Positioing the failed transactions table */ }
                                {props.showFailedTransactionsTable && expanded && stockAdjustmentFailedTransactionsData.length > 0 && (
                                <div style={{border: "1px solid #c10000" , padding: '0px 4px', marginBottom: '14px', marginTop: '14px', borderRadius: '5px'}}>
                                    <h5 className="mt-2 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                                        Failed Transactions: 
                                    </h5>
                                    <div
                                    style={{
                                        maxHeight: stockAdjustmentFailedTransactionsData.length > 10 ? "450px" : "unset", // change based on count
                                        overflowY: stockAdjustmentFailedTransactionsData.length > 10 ? "auto" : "visible",
                                        marginBottom: "4px",
                                        position: "relative",
                                        backgroundColor: "#fff", // helps hide any pixel bleed
                                        borderBottom: "1px solid #dee6ed",
                                    }}
                                    >
                                    <table className="table table-bordered  mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                                        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa", boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.05)"}}>
                                            <tr>
                                                <th style={{ ...thNewStyle, width: "160px"}}>M24l Reference</th>
                                                <th style={thNewStyle}>Message</th>
                                                <th style={thNewStyle}>Last Run</th>
                                                <th style={{ ...thNewStyle, width: "100px"}}> Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        { stockAdjustmentFailedTransactionsData.map((stockAdjustment: IStockAdjustmentsFailedTransaction, index) => (
                                            <tr key={index}>
                                                <td style={tdStyle}>{stockAdjustment.m24l_reference}</td>
                                                <td title={stockAdjustment.extended_message ?? ""} style={{
                                                    ...tdStyle,
                                                    maxWidth: "300px", // adjust as needed
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }} >{stockAdjustment.extended_message}</td>
                                                <td style={tdStyle}>{formatDateFull(stockAdjustment.completed_at ?? "")}</td>
                                                <td style={{...tdStyle}} className="d-flex gap-1">
                                                    {/* Rerun */}
                                                    <button className="btn btn-secondary d-flex align-items-center gap-1" title="Rerun"
                                                         onClick={() => handleFailedStockAdjustmentRerunClick(stockAdjustment)}
                                                    >
                                                        <RotateCcw  style={{ width: "17px", height: "17px" }}/>
                                                    </button>

                                                    {/* Handled */}
                                                    <button className="btn btn-secondary d-flex align-items-center gap-1" title="Handled"
                                                       onClick={() => handleFailedStockAdjustmentHandledClick(stockAdjustment)}
                                                    >
                                                        <CheckCircle style={{ width: "17px", height: "17px" }} />
                                                    </button>
                                                </td>
                                            </tr>
                                           )) }
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{marginBottom: "3px", marginLeft: "10px", color: "#c10000" }}>
                                    <small className="text-muted fw-bolder">Total Failed Transactions: {stockAdjustmentFailedTransactionsData.length}</small>
                                </div>     


                                {/* Modal Failed Transaction Rerun - Start */}
                                <>
                                    <Modal show={stockAdjustmentFailedTransactionsRerunModal} onHide={handleFailedStockAjdustmentRerunResetClick}>
                                        <Modal.Header closeButton>
                                            <Modal.Title>{`Rerun Failed [${selectedStockAdjustmentFailedTransaction?.m24l_reference}] Transaction`}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                            <p>Are you sure you would like to rerun this transaction?</p>
                                            </div>
                                        </Modal.Body>
                                        <Modal.Footer>
                                        <Button variant="primary" onClick={() => { setStockAdjustmentFailedTransactionsRerunModal(false); handleFailedStockAjdustmentRerunSubmit(); } }>
                                            Confirm
                                        </Button>
                                        <Button variant="secondary" onClick={handleFailedStockAjdustmentRerunResetClick}>
                                            Cancel
                                        </Button>
                                        </Modal.Footer>
                                    </Modal>
                                    </> 
                                {/*  Modal Failed Transaction Rerun - End */}
                
                
                                {/* Modal Failed Transaction Handled - Start */}
                                    <>
                                    <Modal show={stockAdjustmentFailedTransactionsHandledModal} onHide={handleFailedStockAjdustmentHandledResetClick}>
                                        <Modal.Header closeButton>
                                            <Modal.Title>{`Handle Failed [${selectedStockAdjustmentFailedTransaction?.m24l_reference}] Transaction`}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                            <p>Are you sure you would like to mark this transaction as handled?</p>
                                            </div>
                                        </Modal.Body>
                                        <Modal.Footer>
                                        <Button variant="primary" onClick={() => { setStockAdjustmentFailedTransactionsHandledModal(false); handleFailedStockAdjustmentHandledSubmit(); } }>
                                            Confirm
                                        </Button>
                                        <Button variant="secondary" onClick={handleFailedStockAjdustmentHandledResetClick}>
                                            Cancel
                                        </Button>
                                        </Modal.Footer>
                                    </Modal>
                                    </> 
                                {/*  Modal Failed Transaction Handled - End */}

                                </div> 
                                )} 


                                <ListControl 
                                  pageNumber={pageNumber} 
                                  pageSize={pageSize} 
                                  totalRecords={totalRecords} 
                                  totalPages={totalPages} 
                                  pageClick={handlePageClick} 
                                  sizeClick={handleSizeClick} 
                                  handleFilterByStatusChange={handleFilterByStatusChange} 
                                  activeStatusKey={activeStatusKey}
                                  onStatusChange={setActiveStatusKey}
                                  />
                            </Card.Header>

                            <Card.Body className="pt-0">
                                <div className="sa-table-container" >
                                <Table size="md" hover className="sa-table">
                                    <ListSearch 
                                     search={search} 
                                     setSearch={setSearch} 
                                     searchClicked={searchClicked} 
                                     clearClicked={clearClicked} 
                                     />
                                    <ListColumns columnDetails={columnDetails} />
                                    <ListRecords 
                                        columnDetails={columnDetails} 
                                        dataRecords={dataRecords} 
                                        errorCategoryName={props.errorCategoryName} 
                                        tableName={props.tableName} 
                                        onCancelTransaction={handleCancelTransaction}
                                    />
                                </Table>
                                </div>
                            </Card.Body>
                        </Card>                    
                    </>
                :                            
                    <>
                        <Card> 
                            <Card.Header>
                                <Card.Title>{props.listTitle}</Card.Title>
                            </Card.Header>

                            <Edit />
                        </Card>
                    </>
                }
            
        </>
    );
    
}

export function formatDate(dateString: string): string {
    var theDate = new Date(dateString);
    return theDate.toLocaleDateString().replaceAll("\/", "-") + " " + theDate.toLocaleTimeString();
}
